
import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { AlertCircle, TrendingDown, TrendingUp } from 'lucide-react';
import { getAdvancedInventoryItems } from '@/services/inventory/inventoryService';

interface InventoryItem {
  id: string;
  name: string;
  current_quantity: number;
  minimum_quantity: number;
  unit_of_measure?: string;
}

interface InventoryAlertsProps {
  storeId: string;
  isLoading: boolean;
  inventory: InventoryItem[];
}

export function InventoryAlerts({ storeId, isLoading, inventory }: InventoryAlertsProps) {
  // Calculate alerts
  const lowStockItems = inventory.filter(item => 
    item.current_quantity > 0 && item.current_quantity < item.minimum_quantity
  );
  
  const outOfStockItems = inventory.filter(item => 
    item.current_quantity <= 0 && item.minimum_quantity > 0
  );

  return (
    <Card className="p-6">
      <h3 className="text-lg font-medium mb-4">Advanced Inventory Alerts</h3>
      
      {isLoading ? (
        <div className="flex items-center justify-center h-40">
          <div className="animate-pulse">Loading alerts...</div>
        </div>
      ) : lowStockItems.length === 0 && outOfStockItems.length === 0 ? (
        <div className="h-40 flex flex-col items-center justify-center gap-2">
          <div className="rounded-full bg-green-100 p-3">
            <TrendingUp className="h-6 w-6 text-green-600" />
          </div>
          <p className="text-muted-foreground">All advanced inventory items are at healthy levels.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {outOfStockItems.map(item => (
            <div key={item.id} className="rounded-md bg-destructive/10 p-3">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <AlertCircle className="h-4 w-4 text-destructive" />
                  <p className="font-medium text-destructive">
                    {item.name}
                  </p>
                </div>
                <Badge variant="outline" className="border-destructive text-destructive">
                  Out of Stock
                </Badge>
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                0 / {item.minimum_quantity} {item.unit_of_measure || 'units'}
              </p>
            </div>
          ))}
          
          {lowStockItems.map(item => (
            <div key={item.id} className="rounded-md bg-amber-500/10 p-3">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <TrendingDown className="h-4 w-4 text-amber-500" />
                  <p className="font-medium text-amber-500">
                    {item.name}
                  </p>
                </div>
                <Badge variant="outline" className="border-amber-500 text-amber-500">
                  Low Stock
                </Badge>
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                {item.current_quantity} / {item.minimum_quantity} {item.unit_of_measure || 'units'} remaining
              </p>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
